import Css from "./style.module.scss";

import { Checkbox, Input, Select } from "nlib/ui";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import DatePickerInput from "../DatePickerInput";
import Environment from "utils/Environment";
import Explanation from "./lib/Explanation";
import FormLabel from "../FormLabel";
import Radio from "nlib/ui/Radio";
import React, { useCallback, useEffect, useMemo } from "react";
import TaskRepeatEndTypes from "const/TaskRepeatEndTypes";
import TaskRepeatPeriods from "const/TaskRepeatPeriods";
import classNames from "classnames";
import moment from "moment";

const MAX_HOURS = 24;

const MAX_DAYS = 31;

const MAX_WEEKS = 4;

const MAX_MONTHS = 12;

const MAX_YEARS = 10;

const MAX_VALUES = {
  [TaskRepeatPeriods.HOUR]: MAX_HOURS,
  [TaskRepeatPeriods.DAY]: MAX_DAYS,
  [TaskRepeatPeriods.WEEK]: MAX_WEEKS,
  [TaskRepeatPeriods.MONTH]: MAX_MONTHS,
  [TaskRepeatPeriods.YEAR]: MAX_YEARS
};

const DEFAULT_VALUES = {
  enabled: false,
  periodType: TaskRepeatPeriods.WEEK,
  periodValue: 1,
  endType: TaskRepeatEndTypes.DATE,
  duePeriodType: TaskRepeatPeriods.WEEK,
  duePeriodValue: 0
};

const TaskRepeatOptions = ({ disabled, className, value = DEFAULT_VALUES, startDate, onChange }) => {
  const {
    enabled,
    periodValue,
    periodType,
    endType,
    endAfterDate,
    endAfterRepetitions,
    occurrencesRemaining,
    duePeriodType,
    duePeriodValue
  } = value;

  const { uiTexts } = useSelector(getTextsData);

  const periodMultiplierOptions = useMemo(() => {
    return Array.from({ length: MAX_VALUES[periodType] }, (item, index) => {
      const number = index + 1;

      return { value: number, label: number };
    });
  }, [periodType]);

  const dueInMultiplierOptions = useMemo(() => {
    return [
      { value: 0, label: uiTexts.none },
      ...Array.from({ length: MAX_VALUES[duePeriodType] }, (item, index) => {
        const number = index + 1;

        return { value: number, label: number };
      })
    ];
  }, [duePeriodType, uiTexts]);

  const periodOptions = useMemo(() => {
    return [
      Environment.checkIsStagingEnv() && { value: TaskRepeatPeriods.HOUR, label: "hours" },
      { value: TaskRepeatPeriods.DAY, label: uiTexts.days },
      { value: TaskRepeatPeriods.WEEK, label: uiTexts.weeks },
      { value: TaskRepeatPeriods.MONTH, label: uiTexts.months },
      { value: TaskRepeatPeriods.YEAR, label: uiTexts.years }
    ].filter(Boolean);
  }, [uiTexts]);

  const handlePeriodValueChange = useCallback((newValue) => {
    onChange({ ...value, periodValue: newValue });
  }, [value, onChange]);

  const handlePeriodTypeChange = useCallback((newValue) => {
    onChange({ ...value, periodType: newValue });
  }, [value, onChange]);

  const handleEndsAfterDateClick = useCallback(() => {
    if (disabled) return;
    onChange({
      ...value,
      endType: TaskRepeatEndTypes.DATE,
      endAfterRepetitions: null
    });
  }, [disabled, value, onChange]);

  const handleEndsAfterOccurrencesClick = useCallback(() => {
    if (disabled) return;
    onChange({
      ...value,
      endType: TaskRepeatEndTypes.OCCURRENCES,
      endAfterDate: null
    });
  }, [disabled, value, onChange]);

  const handleEndsAfterDateChange = useCallback((newValue) => {
    onChange({
      ...value,
      endType: TaskRepeatEndTypes.DATE,
      endAfterDate: newValue ? moment.utc(newValue).toISOString() : null
    });
  }, [onChange, value]);

  const handleEndsAfterOccurrencesChange = useCallback((newValue) => {
    onChange({
      ...value,
      endType: TaskRepeatEndTypes.OCCURRENCES,
      endAfterRepetitions: newValue ? Math.abs(Math.round(newValue)) : ""
    });
  }, [onChange, value]);

  const handleEnabledChange = useCallback(() => {
    onChange({ ...value, enabled: !value.enabled });
  }, [value, onChange]);

  const handleDueDateValueChange = useCallback((newValue) => {
    onChange({ ...value, duePeriodValue: newValue });
  }, [onChange, value]);

  const handleDueDatePeriodChange = useCallback((newValue) => {
    onChange({ ...value, duePeriodType: newValue });
  }, [onChange, value]);

  useEffect(() => {
    if (periodValue > MAX_VALUES[periodType]) {
      onChange({ ...value, periodValue: MAX_VALUES[periodType] });
    }
  }, [onChange, periodType, periodValue, value]);
  useEffect(() => {
    if (duePeriodValue > MAX_VALUES[duePeriodType]) {
      onChange({ ...value, duePeriodValue: MAX_VALUES[duePeriodType] });
    }
  }, [duePeriodType, duePeriodValue, onChange, value]);

  return (
    <div className={classNames(Css.taskRepeatOptions, className)}>
      <FormLabel className={Css.toggleRow}>
        <span>{uiTexts.repeat}</span>
        <Checkbox toggle disabled={disabled} checked={enabled} onChange={handleEnabledChange} />
      </FormLabel>
      {enabled && (
        <>
          <div className={Css.row}>
            <div className={Css.col}>
              <div className={Css.label}>{uiTexts.every}</div>
            </div>
            <div className={Css.colOne}>
              <Select
                topOffset={52}
                disabled={disabled}
                value={periodValue}
                options={periodMultiplierOptions}
                onChange={handlePeriodValueChange} />
            </div>
            <div className={Css.colOne}>
              <Select
                disabled={disabled}
                value={periodType}
                options={periodOptions}
                onChange={handlePeriodTypeChange} />
            </div>
          </div>

          <div className={Css.row}>
            <div className={Css.col}>
              <div
                className={classNames(Css.label, !disabled && Css.cursorPointer)}
                onClick={handleEndsAfterDateClick}>
                <Radio
                  name="endAfterType"
                  className={Css.radio}
                  value={TaskRepeatEndTypes.DATE}
                  checked={endType === TaskRepeatEndTypes.DATE}>
                  <span>{uiTexts.endOn}</span>
                </Radio>
              </div>
            </div>
            <div className={Css.colTwo}>
              <DatePickerInput
                portal
                disabled={disabled}
                placeholder={uiTexts.enterDate}
                invalid={endType === TaskRepeatEndTypes.DATE && !endAfterDate}
                value={endAfterDate || ""}
                onChange={handleEndsAfterDateChange} />
            </div>
          </div>

          <div className={Css.row}>
            <div className={Css.col}>
              <div
                className={classNames(Css.label, !disabled && Css.cursorPointer)}
                onClick={handleEndsAfterOccurrencesClick}>
                <Radio
                  disabled={disabled}
                  name="endAfterType"
                  className={Css.radio}
                  value={TaskRepeatEndTypes.OCCURRENCES}
                  checked={endType === TaskRepeatEndTypes.OCCURRENCES}>
                  <span>{uiTexts.endsAfter}</span>
                </Radio>
              </div>
            </div>
            <div className={Css.colTwo}>
              <Input
                type="number"
                disabled={disabled}
                placeholder={uiTexts.numberOfTasks}
                invalid={endType === TaskRepeatEndTypes.OCCURRENCES && !endAfterRepetitions}
                value={endAfterRepetitions || ""}
                onChange={handleEndsAfterOccurrencesChange} />
            </div>
          </div>

          <div className={Css.row}>
            <div className={Css.col}>
              <div className={Css.label}>{uiTexts.dueIn}</div>
            </div>
            <div className={Css.colOne}>
              <Select
                disabled={disabled}
                value={duePeriodValue}
                options={dueInMultiplierOptions}
                onChange={handleDueDateValueChange} />
            </div>
            <div className={Css.colOne}>
              <Select
                disabled={disabled}
                value={duePeriodType}
                options={periodOptions}
                onChange={handleDueDatePeriodChange} />
            </div>
          </div>
          <Explanation
            className={Css.taskRepeatExplanation}
            startDate={startDate}
            periodValue={periodValue}
            periodType={periodType}
            endType={endType}
            endAfterDate={endAfterDate}
            endAfterRepetitions={endAfterRepetitions}
            occurrencesRemaining={occurrencesRemaining}
            duePeriodType={duePeriodType}
            duePeriodValue={duePeriodValue} />
        </>
      )}
    </div>
  );
};

export default React.memo(TaskRepeatOptions);
